import React, { useMemo, useState } from 'react';
import { Box, ButtonBase, Card, Stack, Tooltip, Typography } from '@mui/material';
import { HeatmapDay } from '../mockStats';
import { heatColor } from './StreakCard';

interface ActivityHeatmapProps {
  byYear: Record<number, HeatmapDay[]>;
  years: number[]; // ordered, newest first
}

const monthNames = [
  'Січ',
  'Лют',
  'Бер',
  'Кві',
  'Тра',
  'Чер',
  'Лип',
  'Сер',
  'Вер',
  'Жов',
  'Лис',
  'Гру',
];

const CELL = 14;
const GAP = 4;
const MONTH_GAP = 14; // visible breathing space between month blocks

interface MonthBlock {
  monthIndex: number;
  label: string;
  // One entry per week column inside this month. Each week is 7 cells in
  // Mon..Sun order; null = leading/trailing padding so a partial first/last
  // week of the month still aligns with the weekday axis.
  weeks: (HeatmapDay | null)[][];
}

// Parse "YYYY-MM-DD" as a LOCAL-time Date. JS's `new Date("2026-01-01")` parses
// the bare-date form as UTC midnight, which in UTC+2/+3 timezones shifts back
// to Dec 31 of the previous year — that's how "Гру" was leaking onto the
// January column header.
const parseLocalDate = (iso: string): Date => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

// Today's date in the same "YYYY-MM-DD" local format used by HeatmapDay.date.
const todayLocalString = (): string => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// Build per-month visual blocks. Each block contains EXACTLY the days of that
// month, broken into Mon..Sun week columns. A week that straddled a month
// boundary used to confuse the eye ("Лют" label over cells that were really
// Jan 26-31). With per-month blocks the boundary is literal: the last week of
// the previous month ends with a Sun pad cell, the first week of the next
// starts with a Mon pad cell, and a visible MONTH_GAP sits between them.
const toMonthBlocks = (days: HeatmapDay[]): MonthBlock[] => {
  if (days.length === 0) return [];

  // JS getDay(): 0=Sun..6=Sat → convert to Mon=0..Sun=6
  const mondayIndex = (jsDay: number) => (jsDay + 6) % 7;

  // Stable iteration order: collect distinct months in encounter order.
  const monthOrder: number[] = [];
  const byMonth = new Map<number, HeatmapDay[]>();
  for (const d of days) {
    const m = parseLocalDate(d.date).getMonth();
    if (!byMonth.has(m)) {
      byMonth.set(m, []);
      monthOrder.push(m);
    }
    byMonth.get(m)!.push(d);
  }

  const blocks: MonthBlock[] = [];
  for (const monthIndex of monthOrder) {
    const monthDays = byMonth.get(monthIndex)!;
    if (monthDays.length === 0) continue;

    const first = parseLocalDate(monthDays[0].date);
    const last = parseLocalDate(monthDays[monthDays.length - 1].date);
    const leadingPad = mondayIndex(first.getDay());
    const trailingPad = 6 - mondayIndex(last.getDay());

    const padded: (HeatmapDay | null)[] = [
      ...Array(leadingPad).fill(null),
      ...monthDays,
      ...Array(trailingPad).fill(null),
    ];

    const weeks: (HeatmapDay | null)[][] = [];
    for (let i = 0; i < padded.length; i += 7) {
      weeks.push(padded.slice(i, i + 7));
    }

    blocks.push({
      monthIndex,
      label: monthNames[monthIndex],
      weeks,
    });
  }

  return blocks;
};

const ActivityHeatmap: React.FC<ActivityHeatmapProps> = ({ byYear, years }) => {
  const [selectedYear, setSelectedYear] = useState(years[0]);
  const days = useMemo(() => byYear[selectedYear] ?? [], [byYear, selectedYear]);
  const today = useMemo(todayLocalString, []);

  const blocks = useMemo(() => toMonthBlocks(days), [days]);

  const totals = useMemo(() => {
    const activeDays = days.filter((d) => d.count > 0).length;
    const minutes = days.reduce((acc, d) => acc + d.minutes, 0);
    const hours = Math.round(minutes / 6) / 10;
    return { activeDays, hours };
  }, [days]);

  return (
    <Card>
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent={'space-between'}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography variant={'h3'}>{'Активність за рік'}</Typography>
            <Typography color={'text.secondary'} variant={'body2'}>
              {'Кожна клітинка — день; інтенсивність — обсяг практики.'}
            </Typography>
          </Box>

          <Stack
            direction={'row'}
            alignItems={'center'}
            spacing={3}
            sx={{ flexShrink: 0 }}
            flexWrap={'wrap'}
            useFlexGap
          >
            <Stat label={'Активних днів'} value={`${totals.activeDays}`} />
            <Stat label={'Загалом'} value={`${totals.hours} год`} />
            <YearTabs
              years={years}
              selected={selectedYear}
              onSelect={setSelectedYear}
            />
          </Stack>
        </Stack>

        <Box sx={{ overflowX: 'auto', pb: 1 }}>
          <HeatmapGrid blocks={blocks} today={today} />
        </Box>

        <Stack
          direction={'row'}
          justifyContent={'flex-end'}
          alignItems={'center'}
          spacing={0.5}
        >
          <Typography variant={'caption'} color={'text.secondary'}>
            {'менше'}
          </Typography>
          {[0, 1, 2, 3, 4].map((lvl) => (
            <Box
              key={lvl}
              sx={(theme) => ({
                bgcolor: heatColor(lvl, theme.palette.mode === 'dark'),
                borderRadius: '2px',
                height: 10,
                width: 10,
              })}
            />
          ))}
          <Typography variant={'caption'} color={'text.secondary'}>
            {'більше'}
          </Typography>
        </Stack>
      </Stack>
    </Card>
  );
};

const HeatmapGrid: React.FC<{ blocks: MonthBlock[]; today: string }> = ({
  blocks,
  today,
}) => (
  <Box>
    <Stack direction={'row'} alignItems={'flex-start'} spacing={0}>
      {/* Weekday axis — rendered once, aligns with all month blocks since
          they all share the Mon..Sun row order. A spacer matches the
          month-label row above. */}
      <Stack
        sx={{
          color: 'text.secondary',
          fontSize: 11,
          pr: 1,
          width: 20,
        }}
      >
        <Box sx={{ height: 14, mb: 0.5 }} />
        {['Пн', '', 'Ср', '', 'Пт', '', 'Нд'].map((d, i) => (
          <Box
            key={i}
            sx={{
              fontSize: 11,
              height: CELL,
              lineHeight: `${CELL}px`,
              marginBottom: i < 6 ? `${GAP}px` : 0,
            }}
          >
            {d}
          </Box>
        ))}
      </Stack>

      {/* Month blocks — each one is self-contained, with its own label and
          grid; visible MONTH_GAP between them communicates the boundary. */}
      <Stack direction={'row'} alignItems={'flex-start'} sx={{ gap: `${MONTH_GAP}px` }}>
        {blocks.map((block) => (
          <Box key={block.monthIndex}>
            <Box sx={{ height: 14, mb: 0.5, textAlign: 'center' }}>
              <Typography
                variant={'caption'}
                color={'text.secondary'}
                sx={{ fontSize: 11, lineHeight: 1 }}
              >
                {block.label}
              </Typography>
            </Box>
            <Stack direction={'row'} alignItems={'flex-start'} sx={{ gap: `${GAP}px` }}>
              {block.weeks.map((week, wi) => (
                <Box
                  key={wi}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: `${GAP}px`,
                  }}
                >
                  {week.map((day, di) => {
                    if (!day) {
                      return (
                        <Box
                          key={`pad-${wi}-${di}`}
                          sx={{
                            height: CELL,
                            width: CELL,
                            visibility: 'hidden',
                          }}
                        />
                      );
                    }
                    const isToday = day.date === today;
                    return (
                      <Tooltip
                        key={day.date}
                        arrow
                        placement={'top'}
                        title={
                          <Box>
                            <Typography variant={'caption'}>
                              {isToday ? `${day.date} · сьогодні` : day.date}
                            </Typography>
                            <br />
                            <Typography variant={'caption'}>
                              {day.minutes
                                ? `${day.minutes} хв активності`
                                : 'без активності'}
                            </Typography>
                          </Box>
                        }
                      >
                        <Box
                          sx={(theme) => ({
                            bgcolor: heatColor(
                              day.count,
                              theme.palette.mode === 'dark',
                            ),
                            borderRadius: '3px',
                            cursor: 'pointer',
                            height: CELL,
                            // `outline` (vs `border`) keeps the cell footprint
                            // identical so today's ring doesn't shift the grid.
                            ...(isToday && {
                              outline: `2px solid ${theme.palette.primary.main}`,
                              outlineOffset: '1px',
                              position: 'relative',
                              zIndex: 1,
                            }),
                            transition: 'transform 80ms ease',
                            width: CELL,
                            '&:hover': { transform: 'scale(1.25)' },
                          })}
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Stack>
  </Box>
);

const YearTabs: React.FC<{
  years: number[];
  selected: number;
  onSelect: (y: number) => void;
}> = ({ years, selected, onSelect }) => (
  <Stack direction={'row'} spacing={1} flexWrap={'wrap'}>
    {years.map((y) => {
      const active = y === selected;
      return (
        <ButtonBase
          key={y}
          onClick={() => onSelect(y)}
          sx={(theme) => ({
            borderRadius: 1.5,
            px: 2,
            py: 0.75,
            minWidth: 64,
            justifyContent: 'center',
            transition: 'background-color 80ms ease, color 80ms ease',
            bgcolor: active
              ? theme.palette.mode === 'dark'
                ? 'rgba(155,124,255,0.18)'
                : 'rgba(59,44,147,0.08)'
              : 'transparent',
            color: active ? 'primary.main' : 'text.primary',
            fontWeight: active ? 700 : 500,
            border: `1px solid ${
              active
                ? theme.palette.primary.main
                : theme.palette.mode === 'dark'
                ? '#303A48'
                : theme.palette.primary.tableBorder
            }`,
            '&:hover': {
              bgcolor:
                theme.palette.mode === 'dark'
                  ? 'rgba(155,124,255,0.12)'
                  : 'rgba(59,44,147,0.05)',
            },
          })}
        >
          <Typography
            variant={'body2'}
            sx={{ fontWeight: 'inherit', color: 'inherit' }}
          >
            {y}
          </Typography>
        </ButtonBase>
      );
    })}
  </Stack>
);

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box>
    <Typography variant={'caption'} color={'text.secondary'}>
      {label}
    </Typography>
    <Typography variant={'h5'} sx={{ color: 'primary.main' }}>
      {value}
    </Typography>
  </Box>
);

export default ActivityHeatmap;
